import { logError } from '@cortex/faro';
import { ORPCError } from '@orpc/client';
import { rpcClient } from './orpc.js';
import type {
  BotStatusLatestResponse,
  ClaudeUsageLatestResponse,
  ClaudeUsageRange,
  CurrentUserResponse,
  LogoutResponse,
  PipelineConfigListResponse,
  PipelineConfig,
  PipelinePermissions,
  SavePipelinePermissionsRequest,
  WorkspacePermission,
  WorkspacePermissionList,
  WorkspacePermissionAdmins,
  UpsertWorkspacePermissionInput,
  RevokeWorkspacePermissionResponse,
  TriggerPipelineResponse,
  DataViewerJobListResponse,
  DataViewerJobDetail,
  QueryResult,
  FilterConditionInput,
  RawFilterConditionInput,
  DataViewerTableListResponse,
  DescribeDataViewerTableResponse,
  PreviewQueryTablesRequest,
  PreviewTableColumnsRequest,
  CreateDataViewerJobRequest,
  UpdateDataViewerJobRequest,
  PreviewJobQueryRequest,
  DatabaseListResponse,
  WorkspaceUser,
  DataViewerJobIdResponse,
  InfraGraphOverviewResponse,
  InfraGraphStackDetailResponse,
  OwnershipTransferListFilter,
  OwnershipTransferListResponse,
  OwnershipTransferRecord,
  MarkOwnershipTransferKeptRequest,
  MarkOwnershipTransferReassignedRequest,
  MarkOwnershipTransferDeletedRequest,
  GenerateThemesResponse,
  GenerateTitleResponse,
  GeneratePagePromptsResponse,
  GenerateImageResponse,
  GenerateCaptionResponse,
  TiktokAuthUrlResponse,
  TiktokCreateDraftResponse,
  TiktokGetStatusResponse,
} from '../types/api.js';

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business APIエラークラス。HTTPステータス・エラーコード付きのカスタムError
 * @graph-connects none
 */
export class ApiError extends Error {
  /** @graph-connects none */
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** @graph-connects none */
const ORPC_STATUS_BY_CODE: Partial<Record<string, number>> = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
};

/**
 * oRPC client error / network error を既存 UI が扱う ApiError へ変換する。
 * @param error 変換対象の error
 * @returns ApiError
 * @graph-connects none
 */
function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }
  if (error instanceof ORPCError) {
    const errorCode = (error as { code?: unknown }).code;
    const code = typeof errorCode === 'string' ? errorCode : undefined;
    const errorStatus = (error as { status?: unknown }).status;
    const status =
      typeof errorStatus === 'number' ? errorStatus : code ? (ORPC_STATUS_BY_CODE[code] ?? 0) : 0;
    return new ApiError(error.message, status, code);
  }
  if (error instanceof Error) {
    return new ApiError(error.message, 0);
  }
  return new ApiError(String(error), 0);
}

/**
 * oRPC procedure を呼び出し、既存 fetch wrapper と同じ 5xx Faro 報告と ApiError 変換を行う。
 * @param operationName feature.procedure 形式の操作名
 * @param operation oRPC procedure 呼び出し
 * @returns oRPC procedure の戻り値
 * @graph-connects /rpc [calls] Cortex Admin API oRPC procedures
 */
async function callRpc<T>(operationName: string, operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    const apiError = toApiError(error);
    if (apiError.status >= 500) {
      logError(apiError, {
        event: 'api_request_failed',
        operation: operationName,
        status: String(apiError.status),
        code: apiError.code ?? 'unknown',
      });
    }
    throw apiError;
  }
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business 認証APIクライアント。getCurrentUser/logoutメソッドで auth oRPC procedures を呼び出す。
 * @graph-connects /rpc [calls] auth.me / auth.logout oRPC procedures
 */
export const authApi = {
  getCurrentUser: async (): Promise<CurrentUserResponse | null> => {
    try {
      return await callRpc('auth.me', () => rpcClient.auth.me());
    } catch (error) {
      // 認証 API 失敗時は未ログイン扱い。UI 側で /login へリダイレクトする
      logError(toApiError(error), {
        event: 'auth_check_failed',
      });
      return null;
    }
  },
  logout: (): Promise<LogoutResponse> => callRpc('auth.logout', () => rpcClient.auth.logout()),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプラインAPIクライアント。getConfigs/getConfig/triggerPipelineメソッドで pipeline oRPC procedures を呼び出す。triggerPipeline は spreadsheet_sync 用に読み取り行数の上書きを渡せる。
 * @graph-connects /rpc [calls] pipelines.list / pipelines.get / pipelines.trigger oRPC procedures
 */
export const pipelineApi = {
  getConfigs: (): Promise<PipelineConfigListResponse> =>
    callRpc('pipelines.list', () => rpcClient.pipelines.list()),
  getConfig: (id: string): Promise<PipelineConfig> =>
    callRpc('pipelines.get', () => rpcClient.pipelines.get({ id })),
  triggerPipeline: (id: string, limitFromEnd?: number): Promise<TriggerPipelineResponse> =>
    callRpc('pipelines.trigger', () =>
      rpcClient.pipelines.trigger({
        id,
        ...(limitFromEnd === undefined ? {} : { limitFromEnd }),
      }),
    ),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business pipeline 管理権限 API クライアント。get/save メソッドで pipelinePermissions oRPC procedures を呼び出す。
 * @graph-connects /rpc [calls] pipelinePermissions.get / pipelinePermissions.save oRPC procedures
 */
export const pipelinePermissionsApi = {
  get: (): Promise<PipelinePermissions> =>
    callRpc('pipelinePermissions.get', () => rpcClient.pipelinePermissions.get()),
  save: (input: SavePipelinePermissionsRequest): Promise<PipelinePermissions> =>
    callRpc('pipelinePermissions.save', () => rpcClient.pipelinePermissions.save(input)),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business workspace user 一覧 API クライアント。pipeline-permissions の picker 候補取得に使う。
 * @graph-connects /rpc [calls] users.list oRPC procedure
 */
export const usersApi = {
  list: (): Promise<WorkspaceUser[]> => callRpc('users.list', () => rpcClient.users.list()),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business Workspace 権限 API クライアント。get/upsert/revoke/saveAdmins で workspacePermissions oRPC procedures を呼び出す。
 * @graph-connects /rpc [calls] workspacePermissions.get / upsert / revoke / saveAdmins oRPC procedures
 */
export const workspacePermissionsApi = {
  get: (): Promise<WorkspacePermissionList> =>
    callRpc('workspacePermissions.get', () => rpcClient.workspacePermissions.get()),
  upsert: (input: UpsertWorkspacePermissionInput): Promise<WorkspacePermission> =>
    callRpc('workspacePermissions.upsert', () => rpcClient.workspacePermissions.upsert(input)),
  revoke: (email: string): Promise<RevokeWorkspacePermissionResponse> =>
    callRpc('workspacePermissions.revoke', () => rpcClient.workspacePermissions.revoke({ email })),
  saveAdmins: (admins: string[]): Promise<WorkspacePermissionAdmins> =>
    callRpc('workspacePermissions.saveAdmins', () =>
      rpcClient.workspacePermissions.saveAdmins({ admins }),
    ),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business クエリ実行 API クライアント。dataViewer oRPC procedures でジョブ一覧・実行・登録・更新・削除を行う。
 * @graph-connects /rpc [calls] dataViewer oRPC procedures
 */
export const dataViewerApi = {
  getJobs: (): Promise<DataViewerJobListResponse> =>
    callRpc('dataViewer.listJobs', () => rpcClient.dataViewer.listJobs()),
  runJob: (
    id: string,
    filters: FilterConditionInput[] = [],
    rawFilters: RawFilterConditionInput[] = [],
  ): Promise<QueryResult> =>
    callRpc('dataViewer.runJob', () =>
      rpcClient.dataViewer.runJob({
        id,
        ...(filters.length > 0 ? { filters } : {}),
        ...(rawFilters.length > 0 ? { rawFilters } : {}),
      }),
    ),
  exportJob: (
    id: string,
    filters: FilterConditionInput[] = [],
    rawFilters: RawFilterConditionInput[] = [],
  ): Promise<QueryResult> =>
    callRpc('dataViewer.exportJob', () =>
      rpcClient.dataViewer.exportJob({
        id,
        ...(filters.length > 0 ? { filters } : {}),
        ...(rawFilters.length > 0 ? { rawFilters } : {}),
      }),
    ),
  createJob: (input: CreateDataViewerJobRequest): Promise<DataViewerJobIdResponse> =>
    callRpc('dataViewer.createJob', () => rpcClient.dataViewer.createJob(input)),
  previewJobQuery: (input: PreviewJobQueryRequest): Promise<QueryResult> =>
    callRpc('dataViewer.previewJobQuery', () => rpcClient.dataViewer.previewJobQuery(input)),
  getJobDetail: (id: string): Promise<DataViewerJobDetail> =>
    callRpc('dataViewer.getJobDetail', () => rpcClient.dataViewer.getJobDetail({ id })),
  updateJob: (id: string, input: UpdateDataViewerJobRequest): Promise<DataViewerJobIdResponse> =>
    callRpc('dataViewer.updateJob', () => rpcClient.dataViewer.updateJob({ id, input })),
  deleteJob: (id: string): Promise<DataViewerJobIdResponse> =>
    callRpc('dataViewer.deleteJob', () => rpcClient.dataViewer.deleteJob({ id })),
  getDatabases: (): Promise<DatabaseListResponse> =>
    callRpc('dataViewer.listDatabases', () => rpcClient.dataViewer.listDatabases()),
  previewQueryTables: (input: PreviewQueryTablesRequest): Promise<DataViewerTableListResponse> =>
    callRpc('dataViewer.previewQueryTables', () => rpcClient.dataViewer.previewQueryTables(input)),
  previewTableColumns: (
    input: PreviewTableColumnsRequest,
  ): Promise<DescribeDataViewerTableResponse> =>
    callRpc('dataViewer.previewTableColumns', () =>
      rpcClient.dataViewer.previewTableColumns(input),
    ),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business infra-graph 構成図 API クライアント。overview（stack 俯瞰）/ stack（stack 内部）の infraGraph oRPC procedures を呼び出す。
 * @graph-connects /rpc [calls] infraGraph.overview / infraGraph.stack oRPC procedures
 */
export const infraGraphApi = {
  getOverview: (): Promise<InfraGraphOverviewResponse> =>
    callRpc('infraGraph.overview', () => rpcClient.infraGraph.overview()),
  getStack: (stack: string): Promise<InfraGraphStackDetailResponse> =>
    callRpc('infraGraph.stack', () => rpcClient.infraGraph.stack({ stack })),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 母艦 bot 稼働状況 API クライアント。botStatus.latest oRPC procedure を呼び出す。
 * @graph-connects /rpc [calls] botStatus.latest oRPC procedure
 */
export const botStatusApi = {
  getLatest: (host?: string): Promise<BotStatusLatestResponse> =>
    callRpc('botStatus.latest', () => rpcClient.botStatus.latest({ host })),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business bot 別 Claude 利用状況 API クライアント。claudeUsage.latest oRPC procedure を呼び出す。
 * @graph-connects /rpc [calls] claudeUsage.latest oRPC procedure
 */
export const claudeUsageApi = {
  getLatest: (range: ClaudeUsageRange): Promise<ClaudeUsageLatestResponse> =>
    callRpc('claudeUsage.latest', () => rpcClient.claudeUsage.latest({ range })),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain HR
 * @graph-business 退職時 cortex リソース移譲監査レコード API クライアント。一覧 / 詳細 / kept / reassigned / deleted の各 oRPC procedure を呼び出す。
 * @graph-connects /rpc [calls] ownershipTransfers.list / get / markKept / markReassigned / markDeleted oRPC procedures
 */
export const ownershipTransfersApi = {
  list: (filter: OwnershipTransferListFilter): Promise<OwnershipTransferListResponse> =>
    callRpc('ownershipTransfers.list', () => rpcClient.ownershipTransfers.list(filter)),
  get: (id: string): Promise<OwnershipTransferRecord> =>
    callRpc('ownershipTransfers.get', () => rpcClient.ownershipTransfers.get({ id })),
  markKept: (input: MarkOwnershipTransferKeptRequest): Promise<OwnershipTransferRecord> =>
    callRpc('ownershipTransfers.markKept', () => rpcClient.ownershipTransfers.markKept(input)),
  markReassigned: (
    input: MarkOwnershipTransferReassignedRequest,
  ): Promise<OwnershipTransferRecord> =>
    callRpc('ownershipTransfers.markReassigned', () =>
      rpcClient.ownershipTransfers.markReassigned(input),
    ),
  markDeleted: (input: MarkOwnershipTransferDeletedRequest): Promise<OwnershipTransferRecord> =>
    callRpc('ownershipTransfers.markDeleted', () =>
      rpcClient.ownershipTransfers.markDeleted(input),
    ),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNS コンテンツ生成 API クライアント。テーマ/タイトル/画像プロンプト/画像生成・ダウンロード記録・TikTok連携の snsContentGenerator oRPC procedures を呼び出す。
 * @graph-connects /rpc [calls] snsContentGenerator.generateThemes / generateTitle / generatePagePrompts / generateImage / recordContentDownload / tiktokAuthUrl / tiktokCallback / tiktokCreateDraft / tiktokGetStatus oRPC procedures
 */
export const snsContentGeneratorApi = {
  generateThemes: (excludeThemes: string[], accountId: string): Promise<GenerateThemesResponse> =>
    callRpc('snsContentGenerator.generateThemes', () =>
      rpcClient.snsContentGenerator.generateThemes({ excludeThemes, accountId }),
    ),
  generateTitle: (theme: string, accountId: string): Promise<GenerateTitleResponse> =>
    callRpc('snsContentGenerator.generateTitle', () =>
      rpcClient.snsContentGenerator.generateTitle({ theme, accountId }),
    ),
  generatePagePrompts: (input: {
    theme: string;
    title: string;
    pageCount: number;
    accountId: string;
  }): Promise<GeneratePagePromptsResponse> =>
    callRpc('snsContentGenerator.generatePagePrompts', () =>
      rpcClient.snsContentGenerator.generatePagePrompts(input),
    ),
  generateImage: (input: {
    contentId: string;
    pageNumber: string;
    accountId: string;
  }): Promise<GenerateImageResponse> =>
    callRpc('snsContentGenerator.generateImage', () =>
      rpcClient.snsContentGenerator.generateImage(input),
    ),
  recordContentDownload: (contentId: string, accountId: string): Promise<void> =>
    callRpc('snsContentGenerator.recordContentDownload', () =>
      rpcClient.snsContentGenerator.recordContentDownload({ contentId, accountId }),
    ).then(() => undefined),
  generateCaption: (input: {
    theme: string;
    title: string;
    accountId: string;
  }): Promise<GenerateCaptionResponse> =>
    callRpc('snsContentGenerator.generateCaption', () =>
      rpcClient.snsContentGenerator.generateCaption(input),
    ),
  tiktokAuthUrl: (accountId: string): Promise<TiktokAuthUrlResponse> =>
    callRpc('snsContentGenerator.tiktokAuthUrl', () =>
      rpcClient.snsContentGenerator.tiktokAuthUrl({ accountId }),
    ),
  tiktokCallback: (input: { code: string; state: string }): Promise<void> =>
    callRpc('snsContentGenerator.tiktokCallback', () =>
      rpcClient.snsContentGenerator.tiktokCallback(input),
    ).then(() => undefined),
  tiktokCreateDraft: (input: {
    contentId: string;
    title: string;
    description: string;
    accountId: string;
  }): Promise<TiktokCreateDraftResponse> =>
    callRpc('snsContentGenerator.tiktokCreateDraft', () =>
      rpcClient.snsContentGenerator.tiktokCreateDraft(input),
    ),
  tiktokGetStatus: (publishId: string, accountId: string): Promise<TiktokGetStatusResponse> =>
    callRpc('snsContentGenerator.tiktokGetStatus', () =>
      rpcClient.snsContentGenerator.tiktokGetStatus({ publishId, accountId }),
    ),
};
